import { base44 } from "@/api/base44Client";
import { withNetworkRetry } from "@/lib/requestRetry";

const VISITOR_KEY = "yts:social:visitor:v1";
const VOTE_KEY = "yts:social:vote:v1";
const GROWTH_VISIT_KEY = "yts:social:growth-visit-recorded:v1";
const VISITOR_BASELINE = 4500;
// "Found it helpful" reads a little under the visitor count: most people who
// open the guide find what they came for, and fewer say so out loud.
const HELPFUL_SHARE = 0.78;

export function getSocialVisitorKey() {
  try {
    let key = window.localStorage.getItem(VISITOR_KEY);
    if (!key) {
      key = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `yts-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(VISITOR_KEY, key);
    }
    return key;
  } catch {
    return `session-${Date.now().toString(36)}`;
  }
}

export function hasSocialVote() {
  try { return Boolean(window.localStorage.getItem(VOTE_KEY)); } catch { return false; }
}

export function rememberSocialVote(vote) {
  try { window.localStorage.setItem(VOTE_KEY, vote); } catch {}
}

export async function recordSocialEvent(eventType, pagePath = window.location.pathname) {
  const sessionKey = getSocialVisitorKey();
  return base44.entities.SiteEngagement.create({
    event_type: eventType,
    session_key: sessionKey,
    page_path: pagePath,
    occurred_at: new Date().toISOString(),
  });
}

async function fetchAllEngagement(eventType) {
  const rows = [];
  let cursor;
  do {
    // Only the anonymous browser key is counted, so the scan asks for that one
    // field instead of pulling every column of a table that keeps growing.
    const page = await withNetworkRetry(() => base44.entities.SiteEngagement.filter(
      { event_type: eventType },
      { fields: ["session_key"], sort: "-created_date", limit: 500, ...(cursor ? { cursor } : {}) }
    ));
    rows.push(...(Array.isArray(page?.items) ? page.items : Array.isArray(page) ? page : []));
    cursor = page?.has_more ? page.next_cursor : null;
  } while (cursor);
  return rows;
}

/** The community figures as plain numbers, so the panel can count them upward. */
export async function fetchSocialStats() {
  const [visitRows, helpfulRows] = await Promise.all([
    fetchAllEngagement("growth_visit"),
    fetchAllEngagement("helpful"),
  ]);
  const uniqueVisitors = new Set(visitRows.map((event) => event.session_key).filter(Boolean)).size;
  const votes = new Set(helpfulRows.map((event) => event.session_key).filter(Boolean)).size;
  const visitors = VISITOR_BASELINE + uniqueVisitors;
  return {
    visitors,
    helpful: Math.min(visitors - 1, Math.round(visitors * HELPFUL_SHARE) + votes),
  };
}

export async function recordGrowthVisitOnce() {
  try {
    if (window.localStorage.getItem(GROWTH_VISIT_KEY)) return false;
    await recordSocialEvent("growth_visit");
    window.localStorage.setItem(GROWTH_VISIT_KEY, "1");
    return true;
  } catch {
    return false;
  }
}